import { DEVICE_NAMES } from "./device-types";

export interface Experiment {
    id: number;
    name: string;
    description: string;
    deviceName: string;
    objectives: string[];
    predefinedPrompts: string[];
}

export const EXPERIMENTS: Experiment[] = [
    // Temperature experiments
    {
        id: 1,
        name: "Endothermic Reactions: Dissolution of Ammonium Nitrate in Water",
        description: `
        An endothermic process is a chemical reaction in which heat is absorbed. When we carry out an endothermic reaction in a flask, it initially cools down.

        Later, heat from the surrounding environment enters the flask until thermal equilibrium is reached.

        In this experiment, we observe the temperature change that occurs during the dissolution of crystalline ammonium nitrate in water. The heat of the reaction can be calculated using the following equation:

        (1) Q = MC∆T
        
        Where:
        Q = Amount of heat released or absorbed
        m = Mass of the substance
        C = Specific heat capacity of the substance
        ∆T = Temperature change

        Equipment
        • Computer
        • Temperature sensor
        • 5 g NH₄NO₃
        • Polystyrene cup
        • Magnetic stirrer and stirring magnet

        Equipment Setup Procedure

        1. Launch the Software program.
        2. Turn on the wireless temperature sensor.
        3. Assemble the equipment as shown in Figure 1.
        Procedure

        1. Prepare a polystyrene lid for the polystyrene cup. The lid should be flat and larger than the diameter of the cup.
        2. Punch a hole in the lid and insert the temperature sensor.
        3. Place the magnetic stir bar into the cup.
        4. Pour 50 ml of tap water into the cup.
        5. Set the cup on the magnetic stirrer.
        6. Place the lid on the cup, leaving a narrow opening to allow for the addition of NH₄NO₃.
        7. Begin stirring the water in the cup.
        8. Press “Start” to begin data recording.
        9. Wait until the sensor readings stabilize.
        10. Add 5 g of NH₄NO₃ to the cup and immediately replace the lid to cover the cup as securely as possible.
        11. Observe the temperature changes in the display window until they are complete.
        `,
        deviceName: DEVICE_NAMES.Temperature,
        objectives: ["Determine the cooling coefficient", "Verify exponential dependence", "Study environmental influence"],
        predefinedPrompts: []
    },
    // PH experiments
    {
        id: 2,
        name: "Natural pH Scale",
        description: "## Detailed Step-by-Step Demonstration Scenario\n\n### Experiment Setup (Physical and Digital)\n\n**Physical Setup**\n\nThe stand includes:\n\n- Five transparent glasses with natural solutions:\n  1. Lemon juice (pH ~4)\n  2. Apple juice (pH ~5)\n  3. Carbonated mineral water (pH ~6.5–8)\n  4. Distilled water (pH ~7)\n  5. Concentrated baking soda solution (pH ~9–10)\n\n- A **robotic arm and a pH sensor with an electrode**. The sensor can be mounted on a stand, while the electrode is fixed to the manipulator of the robotic arm.\n\n- Connection: the pH sensor is connected to a computer or tablet running Windows, on which the experiment software is launched.\n\n**Function of the Robotic Arm**\n\n- The robotic arm only moves the electrode between the glasses according to a predefined sequence.\n- The software does not initiate the arm’s movement and does not alter its behavior algorithm.\n\n**Digital Software Interface**\n\nThe screen displays:\n\n- a measurement control panel (\"Start Measurement\" button);\n- the current digital pH value;\n- a real-time pH change graph;\n- an interaction block with an AI agent featuring pre-prepared “smart” prompts.\n\n---\n\n### Step-by-Step Demonstration\n\n**Step 1 — Visitor Approaches**\n\nThe visitor approaches the stand. Communication occurs exclusively through a specialist. The AI agent does not initiate dialogue and does not greet first.\n\n**Step 2 — Measurement Start**\n\nThe visitor does not need to interact with the glasses — they are already arranged. The robotic arm is launched automatically or by the specialist. In the software, the \"Start Measurement\" button is pressed. The current pH value appears on the screen, and a live graph begins to build.\n\n**Step 3 — First Glass (Lemon Juice)**\n\nThe robotic arm lowers the electrode into the lemon juice. The pH value drops sharply to ~4. A characteristic acidic spike is recorded on the graph. The AI agent does not react and waits for a request.\n\n**Step 4 — Visitor Question**\n\nThe visitor enters a text question or selects a pre-made prompt (for example: “Explain the observed data”). Only after this does the AI agent begin responding.\n\n**Step 5 — Second Glass (Apple Juice)**\n\nBefore each new measurement, the electrode is rinsed in a separate glass with distilled water (cleaning effect between measurements). The electrode is then immersed in apple juice. The pH value gradually rises to ~5, and the graph shows a transition and stabilization.\n\n**Step 6 — Remaining Solutions**\n\nThe process is repeated for the following glasses:\n\n- Mineral water — pH ~6.5–8\n- Distilled water — pH ~7\n- Concentrated baking soda solution — pH ~9–10\n\nFor each solution, electrode immersion, change in readings, and subsequent stabilization are recorded. The visitor may ask questions after each measurement.\n\n**Step 7 — Cycle Completion**\n\nAfter the final measurement, the robotic arm either continues cyclic movements to attract attention or stops — depending on the exhibition settings. A demonstration animation (a small “dance” of the arm) is possible as a symbol of successful experiment completion. The graph contains the full measurement cycle.\n\n**Step 8 — Final Interaction**\n\nThe visitor may ask the AI agent to summarize, compare values, identify anomalies, or relate the experimental results to real-world examples.",
        deviceName: DEVICE_NAMES.PH,
        objectives: [],
        predefinedPrompts: [
            "Explain the observed data",
            "Compare all the beakers",
            "What does the trend on the graph indicate?",
            "Is the sensor working correctly?",
            "Why does the pH value change so quickly?",
            "Explain the difference between acidic and alkaline solutions",
            "Identify a possible measurement error",
            "Explain the next pH value",
            "Relate the data to real-life examples",
            "Provide a brief conclusion"
        ]
    }
];

export const defaultPrompts = [
    "Explain the observed data",
    "Compare all the beakers",
    "What does the trend on the graph indicate?",
    "Provide a brief conclusion"
]

export const getExperimentsByDeviceName = (deviceName: string): Experiment[] => {
    return EXPERIMENTS.filter(experiment => experiment.deviceName === deviceName);
};

export const getExperimentById = (id: number): Experiment | undefined => {
    return EXPERIMENTS.find(experiment => experiment.id === id);
};
